import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DesktopSidebar } from "@/components/DesktopSidebar";
import { ClientNavSpacer } from "@/components/client/ClientNavSpacer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import VarietyField from "@/components/variety-field";
import { getProfileById, type ProfileInfo, uploadAvatar, updateProfile } from "@/integrations/backend/profiles";
import { toast } from "sonner";
import { listNotes, createNote, updateNote, deleteNote, type Note } from "@/integrations/backend/notes";
import { createCustomerPortalSession } from "@/integrations/backend/stripe";
import {
  deleteMyFile,
  listMyFiles,
  uploadMyFile,
  type ClientFileDto,
} from "@/integrations/backend/client-files";
import { LoadRatioChartTitle, LoadRatioWeekChart, type LoadRatioChartPoint } from "@/components/client/LoadRatioWeekChart";
import { Calendar as DateRangeCalendar } from "@/components/ui/calendar";
import type { DateRange } from "react-day-picker";
import {
  User, CheckCircle2, Calendar, MessageSquare,
  Camera, ArrowUpRight, Dumbbell,
  Clipboard, Target, Clock, Zap, Activity, GraduationCap, FileText, Download, Upload, Image as ImageIcon, RotateCcw, Trophy, CalendarDays,
  Pencil, Trash2,
} from "lucide-react";
import LegalTab from "@/components/profile/LegalTab";
import { ProfileMobileHeader } from "@/components/profile/ProfileMobileHeader";
import { ProfileSectionNav } from "@/components/profile/ProfileSectionNav";
import { ProfileSidebarCard } from "@/components/profile/ProfileSidebarCard";
import { EquipmentAccessPanel } from "@/components/profile/EquipmentAccessPanel";
import {
  ProfileTabPanel,
  profileNestedPanelClass,
  profileNestedPanelContentClass,
  profileNestedPanelHeaderClass,
} from "@/components/profile/ProfileTabPanel";
import { VALID_PROFILE_TABS } from "@/components/profile/profile-sections";
import { useEquipment } from "@/hooks/use-equipment";
import DobPicker from "@/components/ui/dob-picker";
import { cn } from "@/lib/utils";
import { format, isValid as isValidDate, parse, parseISO } from "date-fns";
import { parseYYYYMMDDToLocalDate } from "@/lib/date-only";
import { additionalServiceOptions, activityLevelOptions, exerciseTypeOptions, genderOptions, labels, motivationOptions, timeframeOptions, workoutDurationOptions, workoutFrequencyOptions } from "@/lib/onboarding-options";
import { getOnboarding, saveOnboarding as saveOnboardingAPI, type OnboardingData } from "@/integrations/backend/onboarding";
import { getMyChecklistItems, completeChecklistItem, type ChecklistItemToProfile } from "@/integrations/backend/checklist-items";
import { fetchDashboardMetrics } from "@/integrations/backend/dashboard";
import { listWorkoutAssigned } from "@/integrations/backend/workout-assigned";

type OnboardingSummaryView = {
  gender: string | null;
  dob: string | null;
  height: {
    unit: "imperial" | "metric" | null;
    feet: number | null;
    inches: number | null;
    cm: number | null;
  };
  weight: {
    unit: "lb" | "kg" | null;
    value: number | null;
  };
  motivations: string[];
  workoutFrequency: string | null;
  exerciseTypes: string[];
  sportsEnjoyed: string | null;
  variety: string | null;
  timeline: string | null;
  additionalServices: string[];
  trainingForEvent: string | null;
  eventDetails: string | null;
  eventDate: string | null;
  activityLevel: string | null;
  workoutDuration: string | null;
};

const emptyOnboardingSummary: OnboardingSummaryView = {
  gender: null,
  dob: null,
  height: { unit: null, feet: null, inches: null, cm: null },
  weight: { unit: null, value: null },
  motivations: [],
  workoutFrequency: null,
  exerciseTypes: [],
  sportsEnjoyed: null,
  variety: null,
  timeline: null,
  additionalServices: [],
  trainingForEvent: null,
  eventDetails: null,
  eventDate: null,
  activityLevel: null,
  workoutDuration: null,
};

// Helper functions to transform between backend API format and OnboardingSummary format
function onboardingDataToSummary(data: OnboardingData): OnboardingSummaryView {
  return {
    gender: data.gender ?? null,
    dob: data.dob ?? null,
    height: {
      unit: (data.heightUnit as "imperial" | "metric" | null | undefined) ?? null,
      // API now returns numbers, but handle both for backward compatibility
      feet:
        data.heightFeet !== null && data.heightFeet !== undefined
          ? typeof data.heightFeet === "number"
            ? data.heightFeet
            : Number(data.heightFeet)
          : null,
      inches:
        data.heightInches !== null && data.heightInches !== undefined
          ? typeof data.heightInches === "number"
            ? data.heightInches
            : Number(data.heightInches)
          : null,
      cm:
        data.heightCm !== null && data.heightCm !== undefined
          ? typeof data.heightCm === "number"
            ? data.heightCm
            : Number(data.heightCm)
          : null,
    },
    weight: {
      unit: (data.weightUnit as "lb" | "kg" | null | undefined) ?? null,
      // API now returns numbers, but handle both for backward compatibility
      value:
        data.bodyWeight !== null && data.bodyWeight !== undefined
          ? typeof data.bodyWeight === "number"
            ? data.bodyWeight
            : Number(data.bodyWeight)
          : null,
    },
    motivations: Array.isArray(data.motivation) ? data.motivation : [],
    workoutFrequency: data.workoutFrequency ?? null,
    exerciseTypes: Array.isArray(data.exerciseTypes) ? data.exerciseTypes : [],
    sportsEnjoyed: data.sportsEnjoyed ?? null,
    variety: data.variety ?? null,
    timeline: data.resultTimeframe ?? null,
    additionalServices: Array.isArray(data.additionalServices) ? data.additionalServices : [],
    // Event fields and activity level/workout duration
    trainingForEvent: data.trainingForEvent ?? null,
    eventDetails: data.eventDetails ?? null,
    eventDate: data.eventDate ?? null,
    activityLevel: data.activityLevel ?? null,
    workoutDuration: data.workoutDuration ?? null,
  };
}

function normalizeFormToOnboardingPayload(form: {
  gender: string;
  dob: string;
  heightUnit: "imperial" | "metric";
  heightFeet: string;
  heightInches: string;
  heightCm: string;
  weightUnit: "lb" | "kg";
  weightValue: string;
  motivations: string[];
  activityLevel: string;
  workoutFrequency: string;
  workoutDuration: string;
  exerciseTypes: string[];
  sportsEnjoyed: string;
  variety: string;
  timeline: string;
  services: string[];
  trainingForEvent: string;
  eventDetails: string;
  eventDate: string;
}): Partial<OnboardingData> {
  return {
    gender: form.gender || null,
    dob: form.dob || null,
    heightUnit: form.heightUnit || null,
    heightFeet: form.heightUnit === "imperial" ? (form.heightFeet ? Number(form.heightFeet) : null) : null,
    heightInches: form.heightUnit === "imperial" ? (form.heightInches ? Number(form.heightInches) : null) : null,
    heightCm: form.heightUnit === "metric" ? (form.heightCm ? Number(form.heightCm) : null) : null,
    bodyWeight: form.weightValue ? Number(form.weightValue) : null,
    weightUnit: form.weightUnit || null,
    motivation: Array.isArray(form.motivations) ? form.motivations.slice(0, 2) : [],
    activityLevel: form.activityLevel || null,
    workoutFrequency: form.workoutFrequency || null,
    workoutDuration: form.workoutDuration || null,
    exerciseTypes: Array.isArray(form.exerciseTypes) ? form.exerciseTypes : [],
    sportsEnjoyed: form.exerciseTypes?.includes("sports") ? (form.sportsEnjoyed?.trim() ? form.sportsEnjoyed.trim() : null) : null,
    variety: form.variety?.trim() ? form.variety.trim() : "",
    resultTimeframe: form.timeline || null,
    additionalServices: Array.isArray(form.services) ? form.services : [],
    trainingForEvent: form.trainingForEvent || null,
    eventDetails: form.eventDetails || null,
    eventDate: form.eventDate || null,
  };
}

function getNumberProp(obj: unknown, key: string): number | null {
  if (!obj || typeof obj !== "object") return null;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "number" ? v : null;
}

// Dummy data
const profileData = {
  name: "Alex Johnson",
  email: "alex@example.com",
  profilePicture: "https://i.pravatar.cc/300?u=alex", // Placeholder image
  role: "Client",
  joinDate: "August 2023",
  description: "Alex is a dedicated fitness enthusiast focused on building strength and improving overall health. With a busy professional life, Alex values efficient workouts that provide maximum results. Their commitment to consistency and willingness to push boundaries makes them an ideal candidate for progressive training programs.",
  metrics: {
    height: "5'11\"",
    weight: "180 lbs",
    bodyFat: "18%",
    restingHeartRate: "62 bpm",
  },
  goals: [
    { name: "Lose 15 lbs", progress: 60, target: "Target: 165 lbs", current: "Current: 172 lbs" },
    { name: "Bench Press 225 lbs", progress: 80, target: "Target: 225 lbs", current: "Current: 205 lbs" },
    { name: "Run 5K under 25 min", progress: 70, target: "Target: 25 min", current: "Current: 27 min" }
  ],
  checklistItems: [
    { id: 1, title: "Complete Initial Assessment", completed: true },
    { id: 2, title: "Set Fitness Goals", completed: true },
    { id: 3, title: "Upload Profile Picture", completed: false },
    { id: 4, title: "Complete First Workout", completed: true },
    { id: 5, title: "Connect with Trainer", completed: false },
    { id: 6, title: "Schedule Regular Sessions", completed: true },
    { id: 7, title: "Track Nutrition for 7 Days", completed: false },
  ],
  trainerNotes: [
    { id: 1, trainer: "Coach Sarah", date: "2023-05-12", note: "Alex is showing excellent progress in strength training. Recommend increasing weight on squats next session.", profilePic: "https://i.pravatar.cc/150?img=29" },
    { id: 2, trainer: "Coach Michael", date: "2023-05-05", note: "Form on deadlifts needs work. Spent extra time today correcting posture and engaging core properly.", profilePic: "https://i.pravatar.cc/150?img=11" },
    { id: 3, trainer: "Coach Sarah", date: "2023-04-28", note: "Excellent endurance shown in today's HIIT session. Ready to advance to level 3 next week.", profilePic: "https://i.pravatar.cc/150?img=29" },
  ],
  workoutHistory: [
    { date: "2023-05-15", name: "Upper Body Power", completed: "100%", duration: "52 min", intensity: 8, energy: 7, exercises: 8, points: 320 },
    { date: "2023-05-13", name: "Leg Day", completed: "85%", duration: "45 min", intensity: 9, energy: 6, exercises: 6, points: 275 },
    { date: "2023-05-10", name: "Core & Cardio", completed: "100%", duration: "38 min", intensity: 7, energy: 8, exercises: 10, points: 310 },
    { date: "2023-05-08", name: "Full Body Circuit", completed: "95%", duration: "60 min", intensity: 8, energy: 7, exercises: 12, points: 380 },
    { date: "2023-05-05", name: "Back & Shoulders", completed: "100%", duration: "47 min", intensity: 7, energy: 7, exercises: 7, points: 290 },
  ],
  achievements: [
    { id: 1, name: "First Milestone", description: "Completed 10 workouts", icon: "🏆", date: "April 2023" },
    { id: 2, name: "Consistency King", description: "5-day streak", icon: "🔥", date: "May 2023" },
    { id: 3, name: "Weight Warrior", description: "Lifted 1 ton in a session", icon: "💪", date: "May 2023" },
  ],
  assessment: {
    motivations: [
      "Lose Weight", "Build Muscle", "Improve Health"
    ],
    timeframe: "3-6 months",
    successMetrics: [
      { name: "Weight", current: "180 lbs", target: "165 lbs" },
      { name: "Body Fat", current: "18%", target: "12%" },
      { name: "Resting Heart Rate", current: "62 bpm", target: "55 bpm" }
    ],
    trophyGoal: {
      name: "Run a 10K",
      timeline: "6 months",
      importance: "High"
    },
    activityLevel: "Moderately Active",
    workoutFrequency: "3-4 times per week",
    intensityLevel: "Medium to High",
    exercisePreferences: [
      { name: "Strength Training", icon: "💪", selected: true },
      { name: "HIIT", icon: "⚡", selected: true },
      { name: "Running", icon: "🏃", selected: true },
      { name: "Yoga", icon: "🧘", selected: false },
      { name: "Swimming", icon: "🏊", selected: false },
      { name: "Cycling", icon: "🚴", selected: true }
    ],
    gymPersona: "The Focused Achiever",
    personaDescription: "You come to the gym with clear goals and a plan. You're methodical, consistent, and results-driven.",
    personaQualities: ["Disciplined", "Goal-oriented", "Analytical"],
    trainingApproach: "Progressive Overload",
    approachDescription: "Gradually increasing the weight, frequency, or number of repetitions in your strength training routine.",
    focusAreas: ["Upper Body Strength", "Core Stabilization", "Endurance Building"],
    additionalServices: [
      { name: "Nutrition Coaching", selected: true },
      { name: "Recovery Sessions", selected: false },
      { name: "Group Classes", selected: true }
    ],
    improvementAreas: ["Nutrition", "Sleep Quality", "Stress Management"],
    currentObstacles: ["Limited time", "Work stress", "Inconsistent diet"]
  }
};

const ClientProfile = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("checklist");
  const { profile, user, refetchProfile, isTrainer, isAdmin } = useAuth();
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [totalLoggedWorkouts, setTotalLoggedWorkouts] = useState<number>(0);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [trainerProfile, setTrainerProfile] = useState<ProfileInfo | null>(null);
  const [trainerLoading, setTrainerLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Sidebar stats: completed workouts + points from the client's workout log (CompletedWorkout)
  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    (async () => {
      try {
        setMetricsLoading(true);
        const data = await fetchDashboardMetrics();
        if (!active) return;
        setTotalPoints(data.total_points || 0);
        setTotalLoggedWorkouts(data.total_logged_workouts || 0);
      } catch (e) {
        console.error("Failed to load profile metrics", e);
        if (!active) return;
        setTotalPoints(0);
        setTotalLoggedWorkouts(0);
      } finally {
        if (active) setMetricsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  // Load assigned trainer profile (if any)
  useEffect(() => {
    const trainerId = profile?.assigned_trainer_id || null;
    if (!trainerId) {
      setTrainerProfile(null);
      setTrainerLoading(false);
      return;
    }

    let cancelled = false;
    setTrainerLoading(true);
    getProfileById(trainerId)
      .then((tp) => {
        if (cancelled) return;
        setTrainerProfile(tp);
      })
      .catch((e) => {
        console.error("Failed to fetch trainer profile", e);
        if (cancelled) return;
        setTrainerProfile(null);
      })
      .finally(() => {
        if (cancelled) return;
        setTrainerLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profile?.assigned_trainer_id]);
  // Files tab state
  const [clientFiles, setClientFiles] = useState<ClientFileDto[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [filesUploading, setFilesUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<ClientFileDto | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  // Equipment tab state
  const { data: equipment, loading: equipmentLoading } = useEquipment();
  const [equipmentEdit, setEquipmentEdit] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<string[]>([]);
  const [workoutLocation, setWorkoutLocation] = useState<string[]>([]);
  const [locationEdit, setLocationEdit] = useState(false);
  // Track previous location to detect new selections
  const previousLocationRef = useRef<string[]>([]);
  // Onboarding edit state
  const [onboardingSummary, setOnboardingSummary] = useState<OnboardingSummaryView>(emptyOnboardingSummary);
  const [onboardingEdit, setOnboardingEdit] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  // Checklist items state
  const [checklistItems, setChecklistItems] = useState<ChecklistItemToProfile[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(true);
  const [completingItemId, setCompletingItemId] = useState<string | null>(null);
  const [form, setForm] = useState({
    gender: "",
    dob: "",
    heightUnit: "imperial" as "imperial" | "metric",
    heightFeet: "",
    heightInches: "",
    heightCm: "",
    weightUnit: "lb" as "lb" | "kg",
    weightValue: "",
    motivations: [] as string[],
    activityLevel: "",
    workoutFrequency: "",
    workoutDuration: "",
    exerciseTypes: [] as string[],
    sportsEnjoyed: "",
    variety: "",
    timeline: "",
    services: [] as string[],
    trainingForEvent: "",
    eventDetails: "",
    eventDate: "",
  });

  const refreshFiles = async (opts?: { silent?: boolean }) => {
    setFilesError(null);
    if (!opts?.silent) setFilesLoading(true);
    try {
      const rows = await listMyFiles();
      setClientFiles(rows);
    } catch (e) {
      setFilesError(String(e));
    } finally {
      if (!opts?.silent) setFilesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "files") return;

    // Initial load when entering the Files tab.
    void refreshFiles();

    // If a trainer uploads a file while the client is idle, refresh on focus.
    const onFocus = () => void refreshFiles({ silent: true });
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [activeTab]);

  // Map display text to backend codes
  const locationDisplayToCode = (display: string | null): string | null => {
    if (!display) return null;
    const mapping: Record<string, string> = {
      "At Home": "home",
      "Building Gym": "gym",
      "Commercial Gym": "gym",
      "Outdoor / Park": "outdoor",
      "Mixed": "mixed",
    };
    return mapping[display] || display.toLowerCase();
  };

  // Map backend codes to display text
  const locationCodeToDisplay = (code: string): string => {
    const mapping: Record<string, string> = {
      "home": "At Home",
      "gym": "Commercial Gym",
      "outdoor": "Outdoor / Park",
      "mixed": "Mixed",
    };
    return mapping[code] || code;
  };

  // Load onboarding data (including equipment and location) from backend
  useEffect(() => {
    if (!user) {
      setOnboardingLoading(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        setOnboardingLoading(true);
        const data = await getOnboarding();
        if (!active) return;
        
        // Transform backend data to OnboardingSummary format
        const summary = onboardingDataToSummary(data);
        setOnboardingSummary(summary);
        
        // Set equipment IDs and location from backend
        if (data.equipmentIds) {
          setSelectedEquipmentIds(data.equipmentIds);
        }
        const locationDisplayList = (data.location || []).map(locationCodeToDisplay);
        setWorkoutLocation(locationDisplayList);
        previousLocationRef.current = locationDisplayList;
        
        // Seed form from backend data
        setForm({
          gender: summary.gender || "",
          dob: summary.dob || "",
          heightUnit: summary.height.unit || "imperial",
          heightFeet: String(summary.height.feet ?? ""),
          heightInches: String(summary.height.inches ?? ""),
          heightCm: String(summary.height.cm ?? ""),
          weightUnit: summary.weight.unit || "lb",
          weightValue: String(summary.weight.value ?? ""),
          motivations: [...summary.motivations],
          activityLevel: summary.activityLevel || "",
          workoutFrequency: summary.workoutFrequency || "",
          workoutDuration: summary.workoutDuration || "",
          exerciseTypes: [...summary.exerciseTypes],
          sportsEnjoyed: summary.sportsEnjoyed || "",
          variety: summary.variety || "",
          timeline: summary.timeline || "",
          services: [...summary.additionalServices],
          trainingForEvent: summary.trainingForEvent || "",
          eventDetails: summary.eventDetails || "",
          eventDate: summary.eventDate || "",
        });
      } catch (error) {
        console.error("Failed to load onboarding data", error);
        // Keep defaults on error
      } finally {
        if (active) {
          setOnboardingLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  // Fetch checklist items
  useEffect(() => {
    if (!user) {
      setChecklistLoading(false);
      return;
    }
    
    let active = true;
    (async () => {
      try {
        const items = await getMyChecklistItems();
        if (active) {
          // Sort by checklist_item.order
          const sorted = [...items].sort((a, b) => a.checklist_item.order - b.checklist_item.order);
          setChecklistItems(sorted);
        }
      } catch (error) {
        console.error("Failed to load checklist items", error);
        if (active) {
          setChecklistItems([]);
        }
      } finally {
        if (active) {
          setChecklistLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  // Sync tab from URL hash or query (?tab=trainer)
  useEffect(() => {
    try {
      const hash = (location.hash || "").replace("#", "");
      const params = new URLSearchParams(location.search);
      const tab = (params.get("tab") || hash || "").toLowerCase();
      const validTabs = VALID_PROFILE_TABS;
      if (tab && validTabs.has(tab)) {
        setActiveTab(tab);
      }
    } catch {
      /* ignore malformed URL */
    }
  }, [location.hash, location.search]);

  // Auto-switch to legal tab if approval is needed
  useEffect(() => {
    if (profile?.legal_document_approval_needed && activeTab !== "legal") {
      setActiveTab("legal");
      // Update URL to reflect legal tab
      navigate("/client-profile?tab=legal", { replace: true });
    }
  }, [profile?.legal_document_approval_needed, activeTab, navigate]);

  // Prevent tab switching when approval is needed - must stay on legal tab
  const handleTabChange = (value: string) => {
    if (profile?.legal_document_approval_needed && value !== "legal") {
      // Don't allow switching away from legal tab when approval is needed
      return;
    }
    setActiveTab(value);
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Update URL to reflect tab change
    const params = new URLSearchParams(location.search);
    params.set("tab", value);
    navigate(`/client-profile?${params.toString()}`, { replace: true });
  };

  const saveEquipmentPrefs = async (ids: string[]) => {
    try {
      setSelectedEquipmentIds(ids);
      await saveOnboardingAPI({ equipmentIds: ids });
    } catch (error) {
      console.error("Failed to save equipment preferences", error);
    }
  };

  const saveLocation = async (locations: string[]) => {
    try {
      // UI-only: Keep the function but don't worry about persistence
      setWorkoutLocation(locations);
      previousLocationRef.current = locations;
      
      // Note: Actual save logic will be handled later
      // For now, just update the UI state
    } catch (error) {
      console.error("Failed to save location", error);
    }
  };
  
  const handleCompleteChecklistItem = async (itemId: string) => {
    try {
      setCompletingItemId(itemId);
      const updated = await completeChecklistItem(itemId);
      // Update the item in state
      setChecklistItems(prev => 
        prev.map(item => item.id === itemId ? updated : item)
      );
    } catch (error) {
      console.error("Failed to complete checklist item", error);
      // Could show a toast/error message here
    } finally {
      setCompletingItemId(null);
    }
  };

  const saveOnboarding = async () => {
    try {
      const payload = normalizeFormToOnboardingPayload(form);
      await saveOnboardingAPI(payload);
      const refreshed = await getOnboarding();
      setOnboardingSummary(onboardingDataToSummary(refreshed));
    } catch (error) {
      console.error("Failed to save onboarding", error);
    }
  };
  // Notes state
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogMode, setNoteDialogMode] = useState<"create" | "edit">("create");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDialogSaving, setNoteDialogSaving] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [noteDeleting, setNoteDeleting] = useState(false);

  // Fetch notes from API
  useEffect(() => {
    if (!profile?.id) {
      setNotes([]);
      return;
    }
    const userId = profile.id;
    let active = true;
    (async () => {
      try {
        setNotesLoading(true);
        const fetchedNotes = await listNotes(userId);
        if (!active) return;
        setNotes(fetchedNotes);
      } catch (error) {
        console.error("Failed to load notes", error);
        if (active) {
          setNotes([]);
        }
      } finally {
        if (active) {
          setNotesLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  // Filter notes based on privacy and user role
  const visibleNotes = useMemo(() => {
    if (isTrainer || isAdmin) {
      // Trainers and admins can see all notes
      return notes;
    }
    // Clients can only see non-private notes
    return notes.filter((note) => !note.is_private_note_for_trainer_only);
  }, [notes, isTrainer, isAdmin]);

  const canManageNote = (note: Note) => Boolean(profile?.id && note.creator_user_id === profile.id);

  const refreshNotes = async () => {
    if (!profile?.id) return;
    const updatedNotes = await listNotes(profile.id);
    setNotes(updatedNotes);
  };

  const openCreateNoteDialog = () => {
    setNoteDialogMode("create");
    setEditingNoteId(null);
    setNewNote("");
    setNoteDialogOpen(true);
  };

  const openEditNoteDialog = (note: Note) => {
    setNoteDialogMode("edit");
    setEditingNoteId(note.id);
    setNewNote(note.text);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (noteDialogSaving || !profile?.id || !newNote.trim()) return;
    const text = newNote.trim();
    const userId = profile.id;
    const mode = noteDialogMode;
    const editingId = editingNoteId;

    setNoteDialogSaving(true);
    setNoteDialogOpen(false);
    setNewNote("");
    setEditingNoteId(null);

    try {
      if (mode === "create") {
        await createNote({
          client_user_id: userId,
          text,
          is_private_note_for_trainer_only: false,
        });
      } else if (editingId) {
        await updateNote(editingId, { text });
      }
      await refreshNotes();
      toast.success(mode === "create" ? "Note added" : "Note updated");
    } catch (error) {
      console.error("Failed to save note", error);
      setNoteDialogMode(mode);
      setEditingNoteId(editingId);
      setNewNote(text);
      setNoteDialogOpen(true);
      toast.error("Failed to save note");
    } finally {
      setNoteDialogSaving(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete || noteDeleting) return;
    setNoteDeleting(true);
    try {
      await deleteNote(noteToDelete.id);
      await refreshNotes();
      setNoteToDelete(null);
      toast.success("Note deleted");
    } catch (error) {
      console.error("Failed to delete note", error);
      toast.error("Failed to delete note");
    } finally {
      setNoteDeleting(false);
    }
  };

  // Workouts tab: Load chart + date range filter state
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [workoutLogLoading, setWorkoutLogLoading] = useState(false);
  const [workoutLogError, setWorkoutLogError] = useState<string | null>(null);
  const [workoutLog, setWorkoutLog] = useState<
    Array<{
      id: string;
      date: string;
      name: string;
      duration_minutes: number;
      minutes: number | null;
      intensity: number | null;
      energy: number | null;
      points: number | null;
      status: string | null;
    }>
  >([]);
  const [loadRatioSeries, setLoadRatioSeries] = useState<LoadRatioChartPoint[]>([]);
  const [loadRatioWeekStart, setLoadRatioWeekStart] = useState<string | null>(null);
  const [loadSeriesLoading, setLoadSeriesLoading] = useState(false);

  // Fetch workout log (completed workouts)
  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    (async () => {
      try {
        setWorkoutLogLoading(true);
        setWorkoutLogError(null);
        const rows = await listWorkoutAssigned(profile.id);
        if (!active) return;
        const completed = rows
          .filter((w) => Boolean(w.completed_workout) && Boolean(w.date))
          .map((w) => ({
            id: w.id,
            date: w.date,
            name: w.name,
            duration_minutes: w.duration_minutes,
            minutes: w.completed_workout?.minutes ?? null,
            intensity: w.completed_workout?.intensity ?? null,
            energy: w.completed_workout?.energy ?? null,
            points: w.completed_workout?.points ?? null,
            status: w.completed_workout?.status ?? null,
          }))
          .sort((a, b) => (a.date < b.date ? 1 : -1));
        setWorkoutLog(completed);
      } catch (e) {
        console.error("Failed to load workout log", e);
        if (!active) return;
        setWorkoutLog([]);
        setWorkoutLogError(e instanceof Error ? e.message : "Failed to load workout log");
      } finally {
        if (active) setWorkoutLogLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  // Fetch load ratio series for the chart (current week)
  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    (async () => {
      try {
        setLoadSeriesLoading(true);
        const data = await fetchDashboardMetrics();
        if (!active) return;
        setLoadRatioWeekStart(data.load_ratio_week_start ?? null);
        setLoadRatioSeries(
          (data.load_ratio_series || []).map((p) => ({
            date: p.date,
            label: p.label,
            value: p.value ?? 0,
          }))
        );
      } catch (e) {
        console.error("Failed to load workout load series", e);
        if (!active) return;
        setLoadRatioWeekStart(null);
        setLoadRatioSeries([]);
      } finally {
        if (active) setLoadSeriesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  const loadRatioWeekLabel = useMemo(() => {
    if (!loadRatioWeekStart) return null;
    const start = parseYYYYMMDDToLocalDate(loadRatioWeekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `Week of ${format(start, "MMM d")} – ${format(end, "MMM d")}`;
  }, [loadRatioWeekStart]);

  // Filter workout history list by selected date range
  const filteredWorkoutHistory = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return workoutLog;
    const start = new Date(dateRange.from);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange.to);
    end.setHours(23, 59, 59, 999);
    return workoutLog.filter((w) => {
      const d = parseYYYYMMDDToLocalDate(w.date);
      return d >= start && d <= end;
    });
  }, [dateRange, workoutLog]);

  const displayName =
    ((profile?.first_name ?? "") + " " + (profile?.last_name ?? "")).trim() ||
    profile?.email ||
    profileData.name;
  const avatarUrl = profile?.avatar_url ?? "/placeholder.svg";
  const nameInitial = (displayName || "U").trim().charAt(0).toUpperCase();
  const bioText = profile?.bio || "";
  const joinDateText = profile?.created_at
    ? new Date(profile.created_at).toLocaleString(undefined, { month: "long", year: "numeric" })
    : profileData.joinDate;

  const openEditor = () => {
    setFirstName(profile?.first_name || "");
    setLastName(profile?.last_name || "");
    setBio(profile?.bio || "");
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditOpen(true);
  };

  const onAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAvatarFile(file);
    if (file) {
      setAvatarPreview(URL.createObjectURL(file));
    } else {
      setAvatarPreview(null);
    }
  };

  const handleEditOpenChange = (open: boolean) => {
    if (!open && saving) return;
    setEditOpen(open);
  };

  const onSaveProfile = async () => {
    if (!profile || saving) return;
    setSaving(true);
    try {
      let newAvatarUrl: string | undefined;
      if (avatarFile) {
        newAvatarUrl = await uploadAvatar(profile.id, avatarFile, profile.avatar_url || undefined);
      }
      await updateProfile(profile.id, {
        first_name: firstName,
        last_name: lastName,
        bio,
        ...(newAvatarUrl ? { avatar_url: newAvatarUrl } : {}),
      });
      await refetchProfile();
      toast.success("Profile updated");
      setEditOpen(false);
    } catch (e) {
      console.error("Failed to update profile", e);
      const message = e instanceof Error ? e.message : "Unknown error";
      toast.error("Couldn't save profile", { description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen gradient-dark">
      <DesktopSidebar activeTab="home" />
      
      {/* Header — on mobile, profile identity lives in the hero */}
      <div
        className={cn(
          "bg-gradient-to-r from-dark-bg to-dark-card text-white rounded-b-[2.5rem] border-b-2 border-primary/20",
          isMobile ? "px-4 pt-6 pb-6" : "p-6 pt-8 pb-10 sm:pb-16",
          !isMobile && "md:ml-16"
        )}
      >
        <div className="flex items-center justify-between">
          <h1 className={cn("font-bold text-white", isMobile ? "text-xl" : "text-2xl")}>
            My Profile
          </h1>
          <Button
            variant="ghost"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 p-0 hover:bg-primary hover:text-black"
            aria-label="Home"
            onClick={() => navigate("/client-portal")}
          >
            <ArrowUpRight className="h-5 w-5" />
          </Button>
        </div>

        {isMobile && (
          <ProfileMobileHeader
            embeddedInHero
            displayName={displayName}
            joinDateText={joinDateText}
            bioText={bioText}
            avatarUrl={avatarUrl}
            nameInitial={nameInitial}
            completedWorkouts={totalLoggedWorkouts}
            totalPoints={totalPoints}
            metricsLoading={metricsLoading}
            onEditProfile={openEditor}
            onManageSubscription={async () => {
              try {
                const response = await createCustomerPortalSession();
                window.location.href = response.portal_url;
              } catch (error) {
                console.error("Failed to create customer portal session", error);
              }
            }}
          />
        )}
      </div>
      
      {/* Profile content */}
      <div
        className={cn(
          "relative z-10",
          isMobile ? "px-4 pt-4" : "px-4 -mt-10",
          !isMobile && "md:ml-16"
        )}
      >
        <Card
          className={cn(
            "overflow-hidden bg-dark-card",
            isMobile
              ? "border-0 shadow-none rounded-none bg-transparent"
              : "border-primary/20 shadow-card"
          )}
        >
          <div className="flex flex-col md:flex-row">
            {!isMobile && (
            <ProfileSidebarCard
              displayName={displayName}
              joinDateText={joinDateText}
              bioText={bioText}
              avatarUrl={avatarUrl}
              nameInitial={nameInitial}
              completedWorkouts={totalLoggedWorkouts}
              totalPoints={totalPoints}
              metricsLoading={metricsLoading}
              onEditProfile={openEditor}
              onManageSubscription={async () => {
                try {
                  const response = await createCustomerPortalSession();
                  window.location.href = response.portal_url;
                } catch (error) {
                  console.error("Failed to create customer portal session", error);
                }
              }}
            />
            )}

            {/* Main content area with tabs */}
            <div className={cn("flex-grow", isMobile ? "py-0" : "p-4 md:p-6")}>
              {profile?.legal_document_approval_needed && (
                <div className="mb-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-3.5 py-3">
                  <div className="flex items-start gap-2 text-yellow-400">
                    <FileText className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-xs sm:text-sm font-medium">
                      Legal document approval required. Please review and accept the legal documents before continuing.
                    </p>
                  </div>
                </div>
              )}
              <Tabs value={activeTab} className="w-full" onValueChange={handleTabChange}>
                <ProfileSectionNav
                  value={activeTab}
                  onValueChange={handleTabChange}
                  legalApprovalNeeded={profile?.legal_document_approval_needed}
                />
                
                {/* Files Tab */}
                <TabsContent value="files" className="animate-fade-in">
                  <ProfileTabPanel
                    title="Files"
                    icon={<FileText className="w-5 h-5 text-primary" />}
                    description="Your files and files shared by your trainer"
                    headerAction={
                      <Button
                        className="bg-primary text-black hover:bg-primary/90 rounded-full px-4 py-2 h-9 text-sm"
                        disabled={filesUploading}
                        onClick={() => setUploadDialogOpen(true)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                    }
                    contentClassName="space-y-8"
                  >
                      {filesLoading ? (
                        <div className="text-gray-light text-sm">Loading files…</div>
                      ) : filesError ? (
                        <div className="text-sm text-red-400">Failed to load files.</div>
                      ) : (
                        (() => {
                          const myProfileId = profile?.id ?? null;
                          const clientOwned = clientFiles.filter((f) => f.createdByProfileId && f.createdByProfileId === myProfileId);
                          const trainerOwned = clientFiles.filter((f) => !f.createdByProfileId || f.createdByProfileId !== myProfileId);

                          const isImageUrl = (url: string | null) => {
                            if (!url) return false;
                            let path = url;
                            try {
                              path = new URL(url, window.location.origin).pathname;
                            } catch {
                              // fall back to string parsing
                              path = url.split("#")[0]?.split("?")[0] ?? url;
                            }
                            const lower = path.toLowerCase();
                            return (
                              lower.endsWith(".jpg") ||
                              lower.endsWith(".jpeg") ||
                              lower.endsWith(".png") ||
                              lower.endsWith(".gif") ||
                              lower.endsWith(".webp")
                            );
                          };

                          const FileIcon = ({ url }: { url: string | null }) => {
                            if (isImageUrl(url)) return <ImageIcon className="h-4 w-4 text-primary" />;
                            return <FileText className="h-4 w-4 text-primary" />;
                          };

                          const doDownload = (url: string | null, name: string | null) => {
                            if (!url) return;
                            const a = document.createElement("a");
                            a.href = url;
                            a.target = "_blank";
                            a.rel = "noopener noreferrer";
                            if (name) a.download = name;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          };

                          const Section = ({
                            title,
                            files,
                            allowDelete,
                          }: {
                            title: string;
                            files: ClientFileDto[];
                            allowDelete: boolean;
                          }) => (
                            <div className="space-y-3">
                              <div className="text-white font-medium">{title}</div>
                              <div className="overflow-x-auto rounded-lg border border-primary/20">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                      <TableHead className="text-gray-light">File</TableHead>
                                      <TableHead className="text-gray-light">Uploaded</TableHead>
                                      <TableHead className="text-right text-gray-light">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {files.length === 0 ? (
                                      <TableRow className="hover:bg-transparent">
                                        <TableCell colSpan={3} className="text-gray-light text-sm">
                                          No files yet.
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      files.map((f) => (
                                        <TableRow key={f.id} className="hover:bg-dark-slate/60">
                                          <TableCell className="flex items-center gap-2">
                                            <FileIcon url={f.url} />
                                            <span className="text-white">{f.name || "Untitled file"}</span>
                                          </TableCell>
                                          <TableCell className="text-gray-light">
                                            {f.uploadedAt ? new Date(f.uploadedAt).toLocaleString() : "—"}
                                          </TableCell>
                                          <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                                                disabled={!f.url}
                                                onClick={() => {
                                                  setPreviewFile(f);
                                                  setPreviewOpen(true);
                                                }}
                                              >
                                                View
                                              </Button>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                                                disabled={!f.url}
                                                onClick={() => doDownload(f.url, f.name)}
                                              >
                                                <Download className="h-4 w-4 mr-1" />
                                                Download
                                              </Button>
                                              {allowDelete ? (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                                                  onClick={async () => {
                                                    await deleteMyFile(f.id);
                                                    await refreshFiles();
                                                  }}
                                                >
                                                  Delete
                                                </Button>
                                              ) : null}
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          );

                          return (
                            <>
                              <Section title="Client Files" files={clientOwned} allowDelete={true} />
                              <Section title="Trainer Files" files={trainerOwned} allowDelete={false} />
                              <Dialog
                                open={previewOpen}
                                onOpenChange={(open) => {
                                  setPreviewOpen(open);
                                  if (!open) setPreviewFile(null);
                                }}
                              >
                                <DialogContent className="bg-dark-card border-primary/20">
                                  <DialogHeader>
                                    <DialogTitle className="text-white">File preview</DialogTitle>
                                    <DialogDescription className="text-gray-light">
                                      {previewFile?.name || "Untitled file"}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="max-h-[70vh] overflow-auto">
                                    {previewFile?.url && isImageUrl(previewFile.url) ? (
                                      <img
                                        src={previewFile.url}
                                        alt={previewFile.name || "File"}
                                        className="w-full rounded-md border border-primary/20 object-contain"
                                      />
                                    ) : (
                                      <div className="text-gray-light text-sm">Preview not available.</div>
                                    )}
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                                <DialogContent className="bg-dark-card border-primary/20">
                                  <DialogHeader>
                                    <DialogTitle className="text-white">Upload file</DialogTitle>
                                    <DialogDescription className="text-gray-light">
                                      Add a file to your profile.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 pt-2">
                                    <div className="space-y-1">
                                      <Label htmlFor="upload-name" className="text-sm text-gray-light">
                                        File name
                                      </Label>
                                      <Input
                                        id="upload-name"
                                        className="border-primary/20 bg-dark-slate text-white placeholder:text-gray-light/60"
                                        placeholder="e.g. Progress photos – April"
                                        value={uploadName}
                                        onChange={(e) => setUploadName(e.target.value)}
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label htmlFor="upload-file" className="text-sm text-gray-light">
                                        File
                                      </Label>
                                      <Input
                                        id="upload-file"
                                        type="file"
                                        className="border-primary/20 bg-dark-slate text-white"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0] ?? null;
                                          setUploadFile(file);
                                          if (file && !uploadName.trim()) {
                                            setUploadName(file.name);
                                          }
                                        }}
                                      />
                                      {uploadFile && (
                                        <p className="text-xs text-gray-light mt-1">
                                          Selected: <span className="font-medium">{uploadFile.name}</span>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button
                                      variant="outline"
                                      className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                                      onClick={() => {
                                        setUploadDialogOpen(false);
                                        setUploadName("");
                                        setUploadFile(null);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      className="bg-primary text-black hover:bg-primary/90"
                                      disabled={!uploadFile || !uploadName.trim() || filesUploading}
                                      onClick={async () => {
                                        if (!uploadFile) return;
                                        const fileType = uploadFile.type?.startsWith("image/") ? "image" : "file";
                                        setFilesUploading(true);
                                        try {
                                          await uploadMyFile(uploadFile, fileType, uploadName);
                                          // Close/reset the dialog before refreshing to avoid a flicker
                                          // (the files list re-renders during refresh).
                                          setUploadDialogOpen(false);
                                          setUploadName("");
                                          setUploadFile(null);
                                          await refreshFiles({ silent: true });
                                        } finally {
                                          setFilesUploading(false);
                                        }
                                      }}
                                    >
                                      {filesUploading ? "Saving..." : "Save"}
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            </>
                          );
                        })()
                      )}
                  </ProfileTabPanel>
                </TabsContent>

                {/* Equipment Tab */}
                <TabsContent value="equipment" className={cn("animate-fade-in", isMobile ? "space-y-4" : "space-y-6")}>
                  {/* Workout Location */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Activity className="w-5 h-5 mr-2 text-primary" />
                        Workout Location
                      </CardTitle>
                      <CardDescription>
                        <span className="text-gray-light">Where you usually train</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className={cn("space-y-4", profileNestedPanelContentClass(isMobile))}>
                      {workoutLocation.length === 0 && !locationEdit && (
                        <div className={cn(
                          "flex items-center justify-between rounded-lg bg-dark-slate border border-primary/20",
                          isMobile ? "p-3" : "p-4"
                        )}>
                          <div className="text-gray-light">unset</div>
                          <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black" onClick={() => setLocationEdit(true)}>
                            Set Location
                          </Button>
                        </div>
                      )}
                      {workoutLocation.length > 0 && !locationEdit && (
                        <div className="space-y-3">
                          <div className="flex flex-wrap gap-2">
                            {workoutLocation.map((loc) => (
                              <Badge key={loc} variant="default" className="bg-primary/20 text-white border-primary/30 px-3 py-1">
                                {loc}
                              </Badge>
                            ))}
                          </div>
                          <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black" onClick={() => setLocationEdit(true)}>
                            Edit
                          </Button>
                        </div>
                      )}
                      {locationEdit && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 gap-3">
                            {["At Home", "Commercial Gym", "Outdoor / Park"].map((locationOption) => {
                              const checked = workoutLocation.includes(locationOption);
                              return (
                                <label
                                  key={locationOption}
                                  className={cn(
                                    "flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all",
                                    checked
                                      ? "border-primary bg-primary/10"
                                      : "border-primary/20 bg-dark-slate hover:border-primary/50"
                                  )}
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(v) => {
                                      if (v) {
                                        setWorkoutLocation((prev) => [...prev, locationOption]);
                                      } else {
                                        setWorkoutLocation((prev) => prev.filter((loc) => loc !== locationOption));
                                      }
                                    }}
                                  />
                                  <span className="text-white">{locationOption}</span>
                                </label>
                              );
                            })}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              className="bg-primary text-black hover:bg-primary/90"
                              onClick={() => {
                                saveLocation(workoutLocation);
                                setLocationEdit(false);
                              }}
                              disabled={workoutLocation.length === 0}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outline"
                              className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                              onClick={async () => {
                                // Reload from backend to revert changes
                                try {
                                  const data = await getOnboarding();
                                  const locationDisplayList = (data.location || []).map(locationCodeToDisplay);
                                  setWorkoutLocation(locationDisplayList);
                                  previousLocationRef.current = locationDisplayList;
                                } catch {
                                  // Keep current state on error
                                }
                                setLocationEdit(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <EquipmentAccessPanel
                    equipment={equipment}
                    loading={equipmentLoading}
                    selectedIds={selectedEquipmentIds}
                    editMode={equipmentEdit}
                    onEdit={() => setEquipmentEdit(true)}
                    onSave={() => {
                      saveEquipmentPrefs(selectedEquipmentIds);
                      setEquipmentEdit(false);
                    }}
                    onCancel={async () => {
                      try {
                        const data = await getOnboarding();
                        if (data.equipmentIds) {
                          setSelectedEquipmentIds(data.equipmentIds);
                        } else {
                          setSelectedEquipmentIds([]);
                        }
                      } catch {
                        // Keep current state on error
                      }
                      setEquipmentEdit(false);
                    }}
                    onToggle={(id) => {
                      setSelectedEquipmentIds((prev) =>
                        prev.includes(id)
                          ? prev.filter((existingId) => existingId !== id)
                          : Array.from(new Set([...prev, id]))
                      );
                    }}
                    onSelectionChange={setSelectedEquipmentIds}
                  />
                </TabsContent>

                <TabsContent value="onboarding" className={cn("animate-fade-in", isMobile ? "space-y-4" : "space-y-6")}>
                  {/* Edit controls */}
                  <div className="flex items-center justify-end">
                    {!onboardingEdit ? (
                      <Button
                        variant="outline"
                        className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                        onClick={() => setOnboardingEdit(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          className="bg-primary text-black hover:bg-primary/90"
                          onClick={async () => {
                            await saveOnboarding();
                            setOnboardingEdit(false);
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                          onClick={() => {
                            // Reset form from current summary
                            const s = onboardingSummary;
                            setForm({
                              gender: s.gender || "",
                              dob: s.dob || "",
                              heightUnit: s.height.unit || "imperial",
                              heightFeet: String(s.height.feet ?? ""),
                              heightInches: String(s.height.inches ?? ""),
                              heightCm: String(s.height.cm ?? ""),
                              weightUnit: s.weight.unit || "lb",
                              weightValue: String(s.weight.value ?? ""),
                              motivations: [...s.motivations],
                              activityLevel: s.activityLevel || "",
                              workoutFrequency: s.workoutFrequency || "",
                              workoutDuration: s.workoutDuration || "",
                              exerciseTypes: [...s.exerciseTypes],
                              sportsEnjoyed: s.sportsEnjoyed || "",
                              variety: s.variety || "",
                              timeline: s.timeline || "",
                              services: [...s.additionalServices],
                              trainingForEvent: s.trainingForEvent || "",
                              eventDetails: s.eventDetails || "",
                              eventDate: s.eventDate || "",
                            });
                            setOnboardingEdit(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Step 1: Gender, DOB, Height, Weight */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <User className="w-5 h-5 mr-2 text-primary" />
                        Basics
                      </CardTitle>
                      <CardDescription>
                        <span className="text-gray-light">Your core details</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="p-3 rounded-lg bg-dark-slate border border-primary/20">
                            <div className="text-xs text-gray-light">Gender</div>
                            <div className="font-medium text-white capitalize">
                              {onboardingSummary.gender ? onboardingSummary.gender.replace(/-/g, " ") : "Not specified"}
                            </div>
                          </div>
                          <div className="p-3 rounded-lg bg-dark-slate border border-primary/20">
                            <div className="text-xs text-gray-light">Date of Birth</div>
                            <div className="font-medium text-white">
                              {onboardingSummary.dob
                                ? parseYYYYMMDDToLocalDate(onboardingSummary.dob).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })
                                : "Not specified"}
                            </div>
                          </div>
                          <div className="p-3 rounded-lg bg-dark-slate border border-primary/20">
                            <div className="text-xs text-gray-light">Height</div>
                            <div className="font-medium text-white">
                              {onboardingSummary.height.unit === "imperial"
                                ? onboardingSummary.height.feet !== null || onboardingSummary.height.inches !== null
                                  ? `${onboardingSummary.height.feet ?? 0} ft ${onboardingSummary.height.inches ?? 0} in`
                                  : "Not specified"
                                : onboardingSummary.height.unit === "metric"
                                ? onboardingSummary.height.cm !== null
                                  ? `${onboardingSummary.height.cm} cm`
                                  : "Not specified"
                                : "Not specified"}
                            </div>
                          </div>
                          <div className="p-3 rounded-lg bg-dark-slate border border-primary/20">
                            <div className="text-xs text-gray-light">Weight</div>
                            <div className="font-medium text-white">
                              {onboardingSummary.weight.value !== null && onboardingSummary.weight.unit
                                ? `${onboardingSummary.weight.value} ${onboardingSummary.weight.unit === "kg" ? "kg" : "lbs"}`
                                : "Not specified"}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-white">Gender</Label>
                              <Select value={form.gender} onValueChange={(v) => setForm(f => ({ ...f, gender: v }))}>
                                <SelectTrigger className="bg-dark-slate border-primary/20 text-white">
                                  <SelectValue placeholder="Select gender" />
                                </SelectTrigger>
                                <SelectContent className="bg-dark-card border-primary/20">
                                  {genderOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-white">Date of Birth</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full justify-start text-left font-normal bg-dark-slate border-primary/20 text-white",
                                      !form.dob && "text-gray-mid"
                                    )}
                                  >
                                    {(() => {
                                      const iso =
                                        form.dob && /^\d{4}-\d{2}-\d{2}$/.test(form.dob)
                                          ? parseYYYYMMDDToLocalDate(form.dob)
                                          : null;
                                      const us = form.dob ? parse(form.dob, "MM/dd/yyyy", new Date()) : null;
                                      const date = iso && isValidDate(iso) ? iso : (us && isValidDate(us) ? us : null);
                                      return date ? format(date, "MM/dd/yyyy") : "MM/DD/YYYY";
                                    })()}
                                    <Calendar className="ml-auto h-4 w-4 opacity-70" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="p-0 w-[320px] sm:w-[360px]" align="start">
                                  <DobPicker
                                    value={form.dob}
                                    onChange={(iso) => setForm(f => ({ ...f, dob: iso }))}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Height</Label>
                            {form.heightUnit === "metric" ? (
                              <div className="flex items-center gap-3">
                                <div className="relative flex-1">
                                  <Input
                                    type="number"
                                    min={1}
                                    step="1"
                                    placeholder="180"
                                    value={form.heightCm}
                                    onChange={(e) => setForm(f => ({ ...f, heightCm: e.target.value }))}
                                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-14"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">cm</span>
                                </div>
                                <ToggleGroup
                                  type="single"
                                  value={form.heightUnit}
                                  onValueChange={(v) => v && setForm(f => ({ ...f, heightUnit: v as "imperial" | "metric" }))}
                                  className="w-[120px] justify-center bg-dark-slate/60 border border-primary/20 rounded-md"
                                >
                                  <ToggleGroupItem value="imperial" className="px-3 py-2 text-xs">ft/in</ToggleGroupItem>
                                  <ToggleGroupItem value="metric" className="px-3 py-2 text-xs">cm</ToggleGroupItem>
                                </ToggleGroup>
                              </div>
                            ) : (
                              <div className="grid grid-cols-3 gap-3 items-center">
                                <div className="relative">
                                  <Input
                                    type="number"
                                    min={0}
                                    step="1"
                                    placeholder="5"
                                    value={form.heightFeet}
                                    onChange={(e) => setForm(f => ({ ...f, heightFeet: e.target.value }))}
                                    className="w-full bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-10"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">ft</span>
                                </div>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    min={0}
                                    max={11}
                                    step="1"
                                    placeholder="10"
                                    value={form.heightInches}
                                    onChange={(e) => setForm(f => ({ ...f, heightInches: e.target.value }))}
                                    className="w-full bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-10"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">in</span>
                                </div>
                                <ToggleGroup
                                  type="single"
                                  value={form.heightUnit}
                                  onValueChange={(v) => v && setForm(f => ({ ...f, heightUnit: v as "imperial" | "metric" }))}
                                  className="w-[120px] justify-self-end flex justify-center bg-dark-slate/60 border border-primary/20 rounded-md"
                                >
                                  <ToggleGroupItem value="imperial" className="px-3 py-2 text-xs">ft/in</ToggleGroupItem>
                                  <ToggleGroupItem value="metric" className="px-3 py-2 text-xs">cm</ToggleGroupItem>
                                </ToggleGroup>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Weight</Label>
                            <div className="flex items-center gap-3">
                              <div className="relative flex-1">
                                <Input
                                  type="number"
                                  min={1}
                                  step="1"
                                  placeholder={form.weightUnit === "kg" ? "82" : "180"}
                                  value={form.weightValue}
                                  onChange={(e) => setForm(f => ({ ...f, weightValue: e.target.value }))}
                                  className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-14"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">
                                  {form.weightUnit === "kg" ? "kg" : "lbs"}
                                </span>
                              </div>
                              <ToggleGroup
                                type="single"
                                value={form.weightUnit}
                                onValueChange={(v) => v && setForm(f => ({ ...f, weightUnit: v as "lb" | "kg" }))}
                                className="w-[120px] justify-center bg-dark-slate/60 border border-primary/20 rounded-md"
                              >
                                <ToggleGroupItem value="lb" className="px-3 py-2 text-xs">lbs</ToggleGroupItem>
                                <ToggleGroupItem value="kg" className="px-3 py-2 text-xs">kg</ToggleGroupItem>
                              </ToggleGroup>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 2: Motivations */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Target className="w-5 h-5 mr-2 text-primary" />
                        Motivations
                      </CardTitle>
                      <CardDescription>
                        <div className="flex flex-col gap-1">
                          <span className="text-gray-light">Top reasons you’re here</span>
                          {onboardingEdit && (
                            <span className="text-xs text-gray-light">
                              Select up to 2 motivations ({form.motivations.length}/2)
                            </span>
                          )}
                        </div>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        onboardingSummary.motivations.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {onboardingSummary.motivations.map((m) => (
                              <Badge key={m} className="bg-primary/20 text-primary hover:bg-primary/30 px-3 py-1">
                                {labels.motivation[m] ?? m}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-light">Not specified</div>
                        )
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {motivationOptions.map(opt => {
                            const checked = form.motivations.includes(opt.id);
                            const disableNew = !checked && form.motivations.length >= 2;
                            return (
                              <label key={opt.id} className={cn(
                                "flex items-center gap-3 p-3 rounded-xl border",
                                checked ? "border-primary bg-primary/10" : "border-primary/20 bg-dark-slate",
                                disableNew && "opacity-50 cursor-not-allowed"
                              )}>
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    setForm(f => {
                                      const current = new Set(f.motivations);
                                      if (v) {
                                        if (current.size >= 2 && !current.has(opt.id)) return f;
                                        current.add(opt.id);
                                      } else {
                                        current.delete(opt.id);
                                      }
                                      return { ...f, motivations: Array.from(current) };
                                    });
                                  }}
                                  disabled={disableNew}
                                />
                                <span className="text-white">{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 3: Activity Level */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Activity className="w-5 h-5 mr-2 text-primary" />
                        Activity Level
                      </CardTitle>
                      <CardDescription>
                        <span className="text-gray-light">Your current activity level</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        <div className="space-y-2">
                          <div className="font-medium text-white">
                            {onboardingSummary.activityLevel ? labels.activityLevel[onboardingSummary.activityLevel] : "Not specified"}
                          </div>
                          {onboardingSummary.activityLevel && activityLevelOptions.find(opt => opt.value === onboardingSummary.activityLevel) && (
                            <div className="text-sm text-gray-light">
                              {activityLevelOptions.find(opt => opt.value === onboardingSummary.activityLevel)?.description}
                            </div>
                          )}
                        </div>
                      ) : (
                        <RadioGroup value={form.activityLevel} onValueChange={(v) => setForm(f => ({ ...f, activityLevel: v }))} className="grid grid-cols-1 gap-3">
                          {activityLevelOptions.map(opt => (
                            <label key={opt.value} className={cn(
                              "flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                              form.activityLevel === opt.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                            )}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <RadioGroupItem value={opt.value} />
                                  <div className="font-medium">{opt.label}</div>
                                </div>
                                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", form.activityLevel === opt.value ? "bg-primary text-black" : "bg-dark-card border border-primary/20")}>
                                  {form.activityLevel === opt.value ? "✓" : ""}
                                </div>
                              </div>
                              <div className="text-sm text-gray-light mt-2 ml-8">
                                {opt.description}
                              </div>
                            </label>
                          ))}
                        </RadioGroup>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 4: Workout frequency */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Calendar className="w-5 h-5 mr-2 text-primary" />
                        Workout Frequency
                      </CardTitle>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        <div className="space-y-2">
                          <div className="font-medium text-white">
                            {onboardingSummary.workoutFrequency
                              ? (labels.workoutFrequency as Record<string, string>)[onboardingSummary.workoutFrequency] ?? "Not specified"
                              : "Not specified"}
                          </div>
                          {onboardingSummary.workoutDuration && (
                            <div className="text-sm text-gray-light">
                              Duration: {(labels.workoutDuration as Record<string, string>)[onboardingSummary.workoutDuration] ?? onboardingSummary.workoutDuration}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <RadioGroup value={form.workoutFrequency} onValueChange={(v) => setForm(f => ({ ...f, workoutFrequency: v }))} className="grid grid-cols-1 gap-3">
                            {workoutFrequencyOptions.map(opt => (
                              <label key={opt.value} className={cn(
                                "flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                                form.workoutFrequency === opt.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                              )}>
                                <div className="flex items-center gap-3">
                                  <RadioGroupItem value={opt.value} />
                                  <div className="font-medium">{opt.label}</div>
                                </div>
                                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", form.workoutFrequency === opt.value ? "bg-primary text-black" : "bg-dark-card border border-primary/20")}>
                                  {form.workoutFrequency === opt.value ? "✓" : ""}
                                </div>
                              </label>
                            ))}
                          </RadioGroup>
                          {form.workoutFrequency && (
                            <div className="space-y-2">
                              <Label className="text-white">Workout Duration</Label>
                              <RadioGroup value={form.workoutDuration} onValueChange={(v) => setForm(f => ({ ...f, workoutDuration: v }))} className="grid grid-cols-3 gap-3">
                                {workoutDurationOptions.map(opt => (
                                  <label key={opt.value} className={cn(
                                    "flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                                    form.workoutDuration === opt.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                                  )}>
                                    <RadioGroupItem value={opt.value} className="sr-only" />
                                    <span className="font-medium">{opt.label}</span>
                                  </label>
                                ))}
                              </RadioGroup>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Step 4: Exercise types */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Activity className="w-5 h-5 mr-2 text-primary" />
                        Exercise Types You Enjoy
                      </CardTitle>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        onboardingSummary.exerciseTypes.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {onboardingSummary.exerciseTypes.map((t) => (
                              <div key={t} className="p-3 rounded-lg border-2 text-center border-primary/20 bg-dark-slate">
                                <div className="text-sm font-medium text-white">{labels.exerciseType[t] ?? t}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-light">Not specified</div>
                        )
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {exerciseTypeOptions.map(opt => {
                            const checked = form.exerciseTypes.includes(opt.id);
                            return (
                              <label key={opt.id} className={cn(
                                "flex items-center gap-3 p-3 rounded-xl border",
                                checked ? "border-primary bg-primary/10" : "border-primary/20 bg-dark-slate"
                              )}>
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    setForm(f => {
                                      const set = new Set(f.exerciseTypes);
                                      if (v) set.add(opt.id);
                                      else set.delete(opt.id);
                                      const nextExerciseTypes = Array.from(set);
                                      return {
                                        ...f,
                                        exerciseTypes: nextExerciseTypes,
                                        sportsEnjoyed: nextExerciseTypes.includes("sports") ? f.sportsEnjoyed : "",
                                      };
                                    });
                                  }}
                                />
                                <span className="text-white">{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {(onboardingEdit ? form.exerciseTypes.includes("sports") : onboardingSummary.exerciseTypes.includes("sports")) && (
                        <div className="mt-4 space-y-2">
                          <Label className="text-white">Sports you enjoy</Label>
                          {!onboardingEdit ? (
                            <div className="text-sm text-gray-light">
                              {onboardingSummary.sportsEnjoyed?.trim() ? onboardingSummary.sportsEnjoyed : "Not specified"}
                            </div>
                          ) : (
                            <Textarea
                              value={form.sportsEnjoyed}
                              onChange={(e) => setForm((f) => ({ ...f, sportsEnjoyed: e.target.value }))}
                              placeholder="e.g., Soccer, tennis, basketball"
                              className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid min-h-[90px]"
                            />
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Exercise variety */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        Exercise variety
                      </CardTitle>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        <div className="text-sm text-gray-light whitespace-pre-wrap">
                          {onboardingSummary.variety?.trim() ? onboardingSummary.variety : "Not specified"}
                        </div>
                      ) : (
                        <VarietyField
                          id="client-onboarding-variety"
                          value={form.variety}
                          onChange={(next) => setForm((f) => ({ ...f, variety: next }))}
                          label="Exercise variety"
                          labelClassName="text-white"
                          selectTriggerClassName="bg-dark-slate border-primary/20 text-white"
                          textareaClassName="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid min-h-[90px]"
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 5: Timeline */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Clock className="w-5 h-5 mr-2 text-primary" />
                        Timeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        <div className="font-medium text-white">
                          {onboardingSummary.timeline
                            ? (labels.timeframe as Record<string, string>)[onboardingSummary.timeline] ?? "Not specified"
                            : "Not specified"}
                        </div>
                      ) : (
                        <RadioGroup value={form.timeline} onValueChange={(v) => setForm(f => ({ ...f, timeline: v }))} className="grid grid-cols-1 gap-3">
                          {timeframeOptions.map(opt => (
                            <label key={opt.value} className={cn(
                              "flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                              form.timeline === opt.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                            )}>
                              <div className="flex items-center gap-3">
                                <RadioGroupItem value={opt.value} />
                                <div className="font-medium">{opt.label}</div>
                              </div>
                              <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", form.timeline === opt.value ? "bg-primary text-black" : "bg-dark-card border border-primary/20")}>
                                {form.timeline === opt.value ? "✓" : ""}
                              </div>
                            </label>
                          ))}
                        </RadioGroup>
                      )}
                    </CardContent>
                  </Card>

                  {/* Event Training */}
                  <Card className={profileNestedPanelClass(isMobile)}>
                    <CardHeader className={profileNestedPanelHeaderClass(isMobile)}>
                      <CardTitle className="flex items-center text-white">
                        <Trophy className="w-5 h-5 mr-2 text-primary" />
                        Event Training
                      </CardTitle>
                      <CardDescription>
                        <span className="text-gray-light">Are you training for a specific event?</span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className={profileNestedPanelContentClass(isMobile)}>
                      {!onboardingEdit ? (
                        <div className="space-y-4">
                          {onboardingSummary.trainingForEvent === "yes" ? (
                            <>
                              <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                    <Trophy className="w-5 h-5 text-primary" />
                                  </div>
                                  <div className="font-medium text-white">Training for an Event</div>
                                </div>
                                {onboardingSummary.eventDetails && (
                                  <div className="mb-3">
                                    <div className="text-xs text-gray-light mb-1">Event Details</div>
                                    <div className="text-white">{onboardingSummary.eventDetails}</div>
                                  </div>
                                )}
                                {onboardingSummary.eventDate && (
                                  <div className="flex items-center gap-2">
                                    <CalendarDays className="w-4 h-4 text-gray-light" />
                                    <div>
                                      <div className="text-xs text-gray-light">Event Date</div>
                                      <div className="text-white font-medium">
                                        {format(parseYYYYMMDDToLocalDate(onboardingSummary.eventDate), "MMMM d, yyyy")}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          ) : onboardingSummary.trainingForEvent === "no" ? (
                            <div className="p-4 rounded-lg bg-dark-slate border border-primary/20">
                              <div className="text-gray-light">Not training for a specific event</div>
                            </div>
                          ) : (
                            <div className="p-4 rounded-lg bg-dark-slate border border-primary/20">
                              <div className="text-gray-light">No event information provided</div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <RadioGroup 
                            value={form.trainingForEvent || ""} 
                            onValueChange={(value) => {
                              setForm(f => {
                                const newForm = { ...f, trainingForEvent: value };
                                // Clear event details and date if user selects "No"
                                if (value === "no") {
                                  newForm.eventDetails = "";
                                  newForm.eventDate = "";
                                }
                                return newForm;
                              });
                            }}
                            className="grid grid-cols-2 gap-4"
                          >
                            <div>
                              <RadioGroupItem value="yes" id="event-yes-edit" className="peer sr-only" />
                              <Label 
                                htmlFor="event-yes-edit" 
                                className={cn(
                                  "flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                                  form.trainingForEvent === "yes" ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                                )}
                              >
                                <span className="text-2xl mb-1">✅</span>
                                <span className="font-medium">Yes</span>
                              </Label>
                            </div>
                            <div>
                              <RadioGroupItem value="no" id="event-no-edit" className="peer sr-only" />
                              <Label 
                                htmlFor="event-no-edit" 
                                className={cn(
                                  "flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                                  form.trainingForEvent === "no" ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                                )}
                              >
                                <span className="text-2xl mb-1">❌</span>
                                <span className="font-medium">No</span>
                              </Label>
                            </div>
                          </RadioGroup>

                          {form.trainingForEvent === "yes" && (
                            <div className="space-y-4 mt-4 animate-fade-in">
                              <div className="space-y-2">
                                <Label htmlFor="event-details-edit" className="text-white">Event Details</Label>
                                <Textarea
                                  id="event-details-edit"
                                  placeholder="e.g., Marathon in Boston, Powerlifting competition, Triathlon, etc."
                                  value={form.eventDetails || ""}
                                  onChange={(e) => setForm(f => ({ ...f, eventDetails: e.target.value }))}
                                  className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid min-h-[100px]"
                                />
                                <p className="text-xs text-gray-light">Describe the event you're training for</p>
                              </div>

                              <div className="space-y-2">
                                <Label className="text-white">Event Date</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full justify-start text-left font-normal bg-dark-slate border-primary/20 text-white",
                                        !form.eventDate && "text-gray-mid"
                                      )}
                                    >
                                      {form.eventDate ? (
                                        format(parseYYYYMMDDToLocalDate(form.eventDate), "MMMM d, yyyy")
                                      ) : (
                                        "Select event date"
                                      )}
                                      <CalendarDays className="ml-auto h-4 w-4 opacity-70" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="p-0 w-auto" align="start">
                                    <DateRangeCalendar
                                      mode="single"
                                      selected={form.eventDate ? parseYYYYMMDDToLocalDate(form.eventDate) : undefined}
                                      onSelect={(date) => {
                                        if (date) {
                                          setForm(f => ({ ...f, eventDate: format(date, "yyyy-MM-dd") }));
                                        }
                                      }}
                                      disabled={(date) => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        return date < today;
                                      }}
                                      className="bg-dark-card border-primary/20"
                                    />
                                  </PopoverContent>
                                </Popover>
                                <p className="text-xs text-gray-light">When is your event?</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="trainer" className="animate-fade-in">
                  <ProfileTabPanel
                    title="Your Trainer"
                    icon={<GraduationCap className="w-5 h-5 text-primary" />}
                    description="View details of your assigned trainer"
                  >
                      {trainerLoading ? (
                        <div className="text-sm text-gray-light">Loading trainer...</div>
                      ) : trainerProfile ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="w-14 h-14 border-2 border-primary/20">
                              {trainerProfile.avatar_url ? (
                                <AvatarImage src={trainerProfile.avatar_url} alt="Trainer" />
                              ) : (
                                <AvatarFallback className="bg-dark-slate text-white">
                                  {`${trainerProfile.first_name?.[0] || ""}${trainerProfile.last_name?.[0] || ""}`.toUpperCase().slice(0, 2) || "TR"}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div className="flex-1">
                              <div className="text-white font-medium">
                                {`${trainerProfile.first_name || ""} ${trainerProfile.last_name || ""}`.trim() ||
                                  trainerProfile.email ||
                                  "Trainer"}
                              </div>
                              {trainerProfile.email && (
                                <div className="text-gray-light text-sm">{trainerProfile.email}</div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                              onClick={() => navigate("/chat")}
                            >
                              Message
                            </Button>
                          </div>

                          {(trainerProfile.bio || trainerProfile.phone) && (
                            <div className="rounded-lg border border-primary/20 bg-dark-slate p-4">
                              {trainerProfile.bio && (
                                <div className="text-sm text-gray-light whitespace-pre-wrap">{trainerProfile.bio}</div>
                              )}
                              {trainerProfile.phone && (
                                <div className="text-sm text-gray-light mt-2">Phone: {trainerProfile.phone}</div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <Avatar className="w-14 h-14 border-2 border-primary/20">
                            <AvatarImage src={"/placeholder.svg"} alt="Trainer" />
                            <AvatarFallback className="bg-dark-slate text-white">T</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="text-white font-medium">No trainer assigned</div>
                            <div className="text-gray-light text-sm">Your trainer will appear here when assigned.</div>
                          </div>
                        </div>
                      )}
                  </ProfileTabPanel>
                </TabsContent>
                
                {/* Removed legacy Intake tab; replaced by read-only Onboarding summary */}
                
                <TabsContent value="checklist" className="animate-fade-in">
                  <ProfileTabPanel
                    title="Checklist"
                    icon={<CheckCircle2 className="w-5 h-5 text-primary" />}
                    description="Complete these steps to get the most out of your fitness journey"
                  >
                      {checklistLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="text-gray-light text-sm">Loading checklist...</div>
                        </div>
                      ) : checklistItems.length === 0 ? (
                        <div className="text-center py-6 text-sm text-gray-light">
                          No checklist items assigned yet.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium text-white">Progress</span>
                            <span className="text-gray-light">
                              {checklistItems.filter(item => item.is_completed).length} / {checklistItems.length} completed
                            </span>
                          </div>

                          <Progress
                            value={checklistItems.length > 0 ? (checklistItems.filter(item => item.is_completed).length / checklistItems.length) * 100 : 0}
                            className="h-1.5"
                          />

                          <div className={cn(isMobile ? "divide-y divide-white/10" : "space-y-4")}>
                          {checklistItems.map((item) => (
                            <div
                              key={item.id}
                              className={cn(
                                "flex flex-col gap-2.5",
                                isMobile
                                  ? "py-3.5 first:pt-2"
                                  : "gap-3 p-4 border rounded-lg sm:flex-row sm:items-center",
                                !isMobile && (item.is_completed ? "bg-primary/10 border-primary/30" : "bg-dark-slate border-primary/20")
                              )}
                            >
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${item.is_completed ? "bg-primary text-black" : "bg-dark-bg text-gray-light"}`}>
                                  {item.is_completed ? (
                                    <CheckCircle2 className="w-4 h-4" />
                                  ) : (
                                    <span className="text-gray-light text-xs font-bold">{item.checklist_item.order}</span>
                                  )}
                                </div>
                                <span className={`font-medium min-w-0 text-sm sm:text-base ${item.is_completed ? "text-white" : "text-gray-light"}`}>
                                  {item.checklist_item.name}
                                </span>
                              </div>
                              {(item.checklist_item.type === "assessment" && item.checklist_item.assessment_id) || !item.is_completed ? (
                                <div className={cn(
                                  "flex gap-2",
                                  isMobile ? "w-full pl-9" : "flex-col w-full pl-9 sm:pl-0 sm:w-auto sm:flex-row sm:shrink-0"
                                )}>
                                  {item.checklist_item.type === "assessment" && item.checklist_item.assessment_id && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className={cn(
                                        "border-primary/30 text-primary hover:bg-primary hover:text-black",
                                        isMobile ? "h-9 flex-1 text-sm" : "w-full sm:w-auto"
                                      )}
                                      onClick={() => navigate(`/assessments/${item.checklist_item.assessment_id}`)}
                                    >
                                      Open assessment
                                    </Button>
                                  )}
                                  {!item.is_completed && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className={cn(
                                        "border-primary/30 text-primary hover:bg-primary hover:text-black",
                                        isMobile ? "h-9 flex-1 text-sm" : "w-full sm:w-auto"
                                      )}
                                      onClick={() => handleCompleteChecklistItem(item.id)}
                                      disabled={completingItemId === item.id}
                                    >
                                      {completingItemId === item.id ? "Completing..." : "Complete"}
                                    </Button>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ))}
                          </div>
                        </div>
                      )}
                  </ProfileTabPanel>
                </TabsContent>
                
                <TabsContent value="workouts" className="animate-fade-in">
                  <ProfileTabPanel
                    title="Workout History"
                    icon={<Dumbbell className="w-5 h-5 text-primary" />}
                    description="Review your training sessions and track your progress"
                  >
                      <Card className={cn(profileNestedPanelClass(isMobile), isMobile ? "mb-4" : "mb-6")}>
                        <CardHeader className={cn("pb-2", profileNestedPanelHeaderClass(isMobile))}>
                          <CardTitle className="text-white">
                            <LoadRatioChartTitle />
                          </CardTitle>
                        </CardHeader>
                        <CardContent className={profileNestedPanelContentClass(isMobile)}>
                          <LoadRatioWeekChart
                            data={loadRatioSeries}
                            loading={loadSeriesLoading}
                            weekRangeLabel={loadRatioWeekLabel}
                            gradientId="profileLoadRatioArea"
                          />
                        </CardContent>
                      </Card>

                      <div className="flex items-center justify-end gap-2 mb-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black">
                              <Calendar className="mr-2 h-4 w-4" />
                              {dateRange?.from && dateRange?.to
                                ? `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`
                                : "Filter history"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-dark-card border-primary/20 text-white" align="end">
                            <DateRangeCalendar
                              mode="range"
                              numberOfMonths={2}
                              selected={dateRange}
                              onSelect={setDateRange}
                              className="text-white"
                            />
                          </PopoverContent>
                        </Popover>
                        {dateRange?.from && dateRange?.to && (
                          <Button
                            variant="ghost"
                            className="text-gray-light hover:text-white"
                            onClick={() => setDateRange(undefined)}
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reset
                          </Button>
                        )}
                      </div>

                      <div className={cn(isMobile ? "divide-y divide-white/10" : "space-y-4")}>
                        {workoutLogError ? (
                          <div className="text-sm text-red-200 py-4 text-center">
                            Failed to load workout log: {workoutLogError}
                          </div>
                        ) : workoutLogLoading ? (
                          <div className="text-sm text-gray-light py-4 text-center">Loading workout log…</div>
                        ) : filteredWorkoutHistory.length === 0 ? (
                          <div className="text-sm text-gray-light py-4 text-center">No completed workouts yet.</div>
                        ) : filteredWorkoutHistory.map((workout) => (
                          <div
                            key={workout.id}
                            className={cn(
                              isMobile
                                ? "py-3 first:pt-1"
                                : "border rounded-lg p-4 bg-dark-slate hover:bg-dark-slate/80 transition-colors border-primary/20"
                            )}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              {/* Main workout info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                  <h4 className="font-medium text-sm sm:text-base text-white">{workout.name}</h4>
                                </div>
                                
                                {/* Workout details */}
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                                  <div>
                                    <span className="text-gray-light">Date</span>
                                    <p className="font-medium text-white">{workout.date}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-light">Duration</span>
                                    <p className="font-medium text-white">
                                      {Math.round((workout.minutes ?? workout.duration_minutes) || 0)} min
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-light">Intensity</span>
                                    <p className="font-medium text-white">{workout.intensity ?? "-"}/10</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-light">Energy</span>
                                    <p className="font-medium text-white">{workout.energy ?? "-"}/10</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-light">Points</span>
                                    <p className="font-medium text-white">{workout.points ?? "-"}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                  </ProfileTabPanel>
                </TabsContent>
                
                <TabsContent value="notes" className="animate-fade-in">
                  <ProfileTabPanel
                    title="Trainer Notes"
                    icon={<MessageSquare className="w-5 h-5 text-primary" />}
                    description="Feedback and suggestions from your fitness trainers"
                    headerAction={
                      <Button
                        size="sm"
                        className="bg-primary text-black hover:bg-primary/90"
                        onClick={openCreateNoteDialog}
                      >
                        Add Note
                      </Button>
                    }
                    contentClassName="space-y-4"
                  >
                      {notesLoading ? (
                        <div className="text-sm text-gray-light py-4 text-center">Loading notes...</div>
                      ) : visibleNotes.length === 0 ? (
                        <div className="text-sm text-gray-light py-4 text-center">No notes yet. Add your first note above.</div>
                      ) : (
                        <div className={cn(isMobile ? "divide-y divide-white/10" : "space-y-3")}>
                          {visibleNotes.map((note) => (
                            <div
                              key={note.id}
                              className={cn(
                                isMobile
                                  ? "py-3 first:pt-1"
                                  : "border rounded-lg p-4 bg-dark-slate hover:bg-dark-slate/80 transition-colors border-primary/20"
                              )}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-10 w-10">
                                    <AvatarImage src={note.creator_avatar_url || undefined} />
                                    <AvatarFallback>
                                      {note.creator_name.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium text-white">{note.creator_name}</div>
                                    <div className="text-xs text-gray-light">
                                      {note.created_at ? format(parseISO(note.created_at), "MMM d, yyyy 'at' h:mm a") : "Unknown date"}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {note.is_private_note_for_trainer_only && (
                                    <Badge className="bg-primary/20 text-primary text-xs">
                                      Private
                                    </Badge>
                                  )}
                                  {canManageNote(note) && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-light hover:text-white"
                                        onClick={() => openEditNoteDialog(note)}
                                        aria-label="Edit note"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-light hover:text-red-400"
                                        onClick={() => setNoteToDelete(note)}
                                        aria-label="Delete note"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                              <p className="text-gray-light whitespace-pre-wrap">{note.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                  </ProfileTabPanel>
                </TabsContent>
                
                {/* Legal Tab (last) */}
                <TabsContent value="legal" className="animate-fade-in">
                  <LegalTab />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </Card>
        {isMobile && <ClientNavSpacer />}
      </div>

      <Dialog open={editOpen} onOpenChange={handleEditOpenChange}>
        <DialogContent
          className="w-[calc(100%-2rem)] max-w-lg rounded-2xl border border-primary/20 bg-dark-card text-white shadow-card max-h-[90vh] flex flex-col overflow-hidden z-[60] p-0 gap-0 [&>button]:text-gray-light [&>button]:hover:text-white"
          onPointerDownOutside={(e) => {
            if (saving) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (saving) e.preventDefault();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription className="text-gray-light">
              Update your profile details and profile picture.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col flex-1 min-h-0"
            onSubmit={(e) => {
              e.preventDefault();
              void onSaveProfile();
            }}
          >
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-2 grid gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm text-gray-light">First Name</label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="bg-dark-slate border-primary/20 text-white" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-light">Last Name</label>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} className="bg-dark-slate border-primary/20 text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-light">Bio</label>
                <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} className="bg-dark-slate border-primary/20 text-white" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-light">Profile Image</label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Avatar className="w-16 h-16 border-2 border-primary/20 shrink-0">
                    <AvatarImage src={avatarPreview || avatarUrl} alt={displayName} />
                    <AvatarFallback className="text-lg bg-dark-slate text-white">
                      {nameInitial}
                    </AvatarFallback>
                  </Avatar>
                  <Input type="file" accept="image/*" onChange={onAvatarChange} className="bg-dark-slate border-primary/20 text-white" />
                </div>
              </div>
            </div>
            <DialogFooter className="border-t border-primary/20 px-6 py-4 shrink-0 flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto border-primary/30 text-primary hover:bg-primary hover:text-black"
                disabled={saving}
                onClick={() => handleEditOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="w-full sm:w-auto bg-primary text-black hover:bg-primary/90" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onOpenChange={(open) => {
          if (!open && noteDialogSaving) return;
          setNoteDialogOpen(open);
          if (!open) {
            setNewNote("");
            setEditingNoteId(null);
            setNoteDialogMode("create");
          }
        }}
      >
        <DialogContent
          className="bg-dark-card text-white border-primary/20"
          onPointerDownOutside={(e) => {
            if (noteDialogSaving) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (noteDialogSaving) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{noteDialogMode === "create" ? "Add Note" : "Edit Note"}</DialogTitle>
            <DialogDescription className="text-gray-light">
              {noteDialogMode === "create"
                ? "Add a note to your profile."
                : "Update your note."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={5}
              placeholder="Write your note..."
              className="bg-dark-slate border-primary/20 text-white"
              disabled={noteDialogSaving}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary hover:text-black"
              disabled={noteDialogSaving}
              onClick={() => {
                setNewNote("");
                setNoteDialogOpen(false);
                setEditingNoteId(null);
                setNoteDialogMode("create");
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-primary text-black hover:bg-primary/90"
              disabled={!newNote.trim() || !profile?.id || noteDialogSaving}
              onClick={() => void handleSaveNote()}
            >
              {noteDialogSaving ? "Saving..." : "Save Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(noteToDelete)} onOpenChange={(open) => !open && setNoteToDelete(null)}>
        <AlertDialogContent className="bg-dark-card text-white border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-light">
              This note will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={noteDeleting}
              className="border-primary/30 text-primary hover:bg-primary hover:text-black"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={noteDeleting}
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteNote();
              }}
            >
              {noteDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ClientProfile;
